import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { schedulingApi } from "./api/scheduling";
import { qk } from "../../lib/queryKeys";
import {
  CreateSchedulingItemPayload,
  UpdateSchedulingItemPayload,
  UpdateMultipleSchedulingPayload,
  SchedulingData,
  SchedulingItem,
  CreateLinkPayload,
  UpdateLinkPayload,
  SchedulingLink,
} from "./types/scheduling";
import { queryClient } from "../../shared/providers/QueryProvider";

export const useGetSchedules = (projectId: number) => {

  const { data, isLoading, isPending, isError, refetch } = useQuery({
    queryKey: qk.scheduling.byProject(projectId),
    queryFn: () => schedulingApi.getSchedules(projectId),
    enabled: !!projectId,
  });

  return {
    data,
    isLoading: isLoading || isPending,
    isError: isError,
    error: isError ? "Failed to load schedules." : null,
    refetch,
    removeCache: () => { queryClient.removeQueries({ queryKey: qk.scheduling.byProject(projectId) }) }
  };

};

export const useGetSchedule = (scheduleId: number | undefined) => {

  const { data, isLoading, isPending, isSuccess, isRefetching, isError, error } = useQuery({
    queryKey: ["schedule", scheduleId],
    queryFn: () => schedulingApi.getSchedule(scheduleId),
    enabled: !!scheduleId,
  });

  return {
    data: isSuccess ? data.data.row : null,
    isLoading: isLoading || isPending || isRefetching,
    isError: isError,
    error,
    removeCache: () => {
      queryClient.removeQueries({ queryKey: ["schedule"] });
    }
  };

};

export const useCreateSchedule = (projectId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateSchedulingItemPayload) =>
      schedulingApi.createSchedule(projectId, payload),
    onMutate: async (newItem) => {
      await queryClient.cancelQueries({
        queryKey: qk.scheduling.byProject(projectId),
      });

      const previousData = queryClient.getQueryData<{
        data: SchedulingData;
      }>(qk.scheduling.byProject(projectId));

      if (previousData) {
        const optimisticItem: SchedulingItem = {
          id: Date.now(),
          project_id: projectId,
          parent_id: newItem.parent_id,
          name: newItem.name,
          category: newItem.category || null,
          stage_gate_id: newItem.stage_gate_id || null,
          phase_type_id: newItem.phase_type_id || null,
          planned_start_date: newItem.planned_start_date || null,
          planned_end_date: newItem.planned_end_date || null,
          actual_start_date: null,
          actual_end_date: null,
          status: newItem.status || "not_started",
          progress_percentage: newItem.progress_percentage || "0",
          can_start: true,
          budget_allocated: newItem.budget_allocated || null,
          user_id: newItem.user_id || null,
          assigned_to: null,
          role_id: newItem.role_id || null,
          created_by: null,
          created_at: new Date().toISOString(),
          updated_by: null,
          updated_at: new Date().toISOString(),
          sequence_order: previousData.data.rows.length + 1,
          color: newItem.color || null,
          type: newItem.type,
          owner: "",
          isEditable: true,
          isDeletable: true,
          description: newItem.description || null,
          is_recurring: false,
          frequency: null,
          recurring_day: [],
          is_decision_approved: false,
          is_action_owner: false,
          asset_id: null,
          monthly_pattern: null,
        };

        queryClient.setQueryData(qk.scheduling.byProject(projectId), {
          ...previousData,
          data: {
            ...previousData.data,
            rows: [...previousData.data.rows, optimisticItem],
          },
        });
      }

      return { previousData };
    },
    onError: (_err, _newItem, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          qk.scheduling.byProject(projectId),
          context.previousData
        );
      }
    },
    onSuccess: async(response) => {
      if (response?.data?.row) {
        const newRow = response.data.row;
        queryClient.setQueryData<{ data: SchedulingData }>(
          qk.scheduling.byProject(projectId),
          (old) => {
            if (!old) return old;

            const rowExists = old.data.rows.some(r => r.id === newRow.id);
            return {
              ...old,
              data: {
                ...old.data,
                rows: rowExists
                  ? old.data.rows.map((row) => (row.id === newRow.id ? newRow : row))
                  : [...old.data.rows, newRow],
              },
            };
          }
        );
      }
      await queryClient.refetchQueries({ queryKey: ["tasks"] });
    },
  });
};

export const useUpdateSchedule = (projectId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      scheduleId,
      payload,
    }: {
      scheduleId: number;
      payload: Partial<UpdateSchedulingItemPayload>;
    }) => schedulingApi.updateSchedule(projectId, scheduleId, payload),
    onMutate: async ({ scheduleId, payload }) => {
      await queryClient.cancelQueries({
        queryKey: qk.scheduling.byProject(projectId),
      });

      const previousData = queryClient.getQueryData<{
        data: SchedulingData;
      }>(qk.scheduling.byProject(projectId));

      if (previousData) {
        queryClient.setQueryData(qk.scheduling.byProject(projectId), {
          ...previousData,
          data: {
            ...previousData.data,
            rows: previousData.data.rows.map((row) =>
              row.id === scheduleId
                ? {
                  ...row,
                  ...payload,
                  updated_at: new Date().toISOString(),
                }
                : row
            ),
          },
        });
      }

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          qk.scheduling.byProject(projectId),
          context.previousData
        );
      }
    },
    onSuccess: (response, { scheduleId }) => {
      if (response?.data?.row) {
        const updatedRow = response.data.row;
        queryClient.setQueryData<{ data: SchedulingData }>(
          qk.scheduling.byProject(projectId),
          (old) => {
            if (!old) return old;
            const newRows = old.data.rows.map((row) =>
              row.id === scheduleId ? updatedRow : row
            );
            return {
              ...old,
              data: {
                ...old.data,
                rows: newRows,
              },
            };
          }
        );
      }
    },
  });
};

export const useUpdateMultipleSchedules = (projectId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateMultipleSchedulingPayload) =>
      schedulingApi.updateMultipleSchedules(projectId, payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({
        queryKey: qk.scheduling.byProject(projectId),
      });

      const previousData = queryClient.getQueryData<{
        data: SchedulingData;
      }>(qk.scheduling.byProject(projectId));

      if (previousData) {
        const updatesMap = new Map(
          payload.items.map((item) => [item.id, item])
        );

        queryClient.setQueryData(qk.scheduling.byProject(projectId), {
          ...previousData,
          data: {
            ...previousData.data,
            rows: previousData.data.rows.map((row) => {
              const update = updatesMap.get(row.id);
              return update
                ? {
                  ...row,
                  ...update,
                  updated_at: new Date().toISOString(),
                }
                : row;
            }),
          },
        });
      }

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          qk.scheduling.byProject(projectId),
          context.previousData
        );
      }
    },
  });
};

export const useDeleteSchedule = (projectId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleId: number) =>
      schedulingApi.deleteSchedule(projectId, scheduleId),
    onMutate: async (scheduleId) => {
      await queryClient.cancelQueries({
        queryKey: qk.scheduling.byProject(projectId),
      });

      const previousData = queryClient.getQueryData<{
        data: SchedulingData;
      }>(qk.scheduling.byProject(projectId));

      if (previousData) {
        queryClient.setQueryData(qk.scheduling.byProject(projectId), {
          ...previousData,
          data: {
            ...previousData.data,
            rows: previousData.data.rows.filter((row) => row.id !== scheduleId),
          },
        });
      }

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          qk.scheduling.byProject(projectId),
          context.previousData
        );
      }
    },
  });
};

export const useCreateLink = (projectId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateLinkPayload) =>
      schedulingApi.createLink(projectId, payload),
    onMutate: async (newLink) => {
      await queryClient.cancelQueries({
        queryKey: qk.scheduling.byProject(projectId),
      });

      const previousData = queryClient.getQueryData<{
        data: SchedulingData;
      }>(qk.scheduling.byProject(projectId));

      if (previousData) {
        const optimisticLink: SchedulingLink = {
          id: Date.now(),
          project_id: projectId.toString(),
          source_id: newLink.source_id.toString(),
          target_id: newLink.target_id.toString(),
          dependency_type: newLink.dependency_type,
          created_at: new Date().toISOString(),
          created_by: null,
          updated_at: new Date().toISOString(),
          updated_by: null,
        };

        queryClient.setQueryData(qk.scheduling.byProject(projectId), {
          ...previousData,
          data: {
            ...previousData.data,
            links: [...previousData.data.links, optimisticLink],
          },
        });
      }

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          qk.scheduling.byProject(projectId),
          context.previousData
        );
      }
    },
  });
};

export const useUpdateLink = (projectId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      linkId,
      payload,
    }: {
      linkId: number;
      payload: UpdateLinkPayload;
    }) => schedulingApi.updateLink(projectId, linkId, payload),
    onMutate: async ({ linkId, payload }) => {
      await queryClient.cancelQueries({
        queryKey: qk.scheduling.byProject(projectId),
      });

      const previousData = queryClient.getQueryData<{
        data: SchedulingData;
      }>(qk.scheduling.byProject(projectId));

      if (previousData) {
        queryClient.setQueryData(qk.scheduling.byProject(projectId), {
          ...previousData,
          data: {
            ...previousData.data,
            links: previousData.data.links.map((link) =>
              link.id === linkId
                ? {
                  ...link,
                  ...(payload.source_id && { source_id: payload.source_id.toString() }),
                  ...(payload.target_id && { target_id: payload.target_id.toString() }),
                  ...(payload.dependency_type && { dependency_type: payload.dependency_type }),
                  updated_at: new Date().toISOString(),
                }
                : link
            ),
          },
        });
      }

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          qk.scheduling.byProject(projectId),
          context.previousData
        );
      }
    },
  });
};

export const useDeleteLink = (projectId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (linkId: number) => schedulingApi.deleteLink(projectId, linkId),
    onMutate: async (linkId) => {
      await queryClient.cancelQueries({
        queryKey: qk.scheduling.byProject(projectId),
      });

      const previousData = queryClient.getQueryData<{
        data: SchedulingData;
      }>(qk.scheduling.byProject(projectId));

      if (previousData) {
        queryClient.setQueryData(qk.scheduling.byProject(projectId), {
          ...previousData,
          data: {
            ...previousData.data,
            links: previousData.data.links.filter((link) => link.id !== linkId),
          },
        });
      }

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          qk.scheduling.byProject(projectId),
          context.previousData
        );
      }
    },
  });
};

